import { FunctionComponent, useContext } from 'react';
import { useFragment, graphql } from 'react-relay';
import { FormattedMessage } from 'dibs-react-intl';
import classnames from 'classnames';
import dibsCss from 'dibs-css';
import { isOnReviewPane, isPaneClickable } from './reducers/checkoutPaneReducer';
import { CheckoutServerVarsContext } from './components/shared/CheckoutServerVarsContext';
import { useCheckoutSelector } from './reducers/root';
import { PANES } from './constants/checkoutConstants';
import { CheckoutAddressReview } from './shared/CheckoutAddressReview';
import { CheckoutDeliveryReview } from './shared/CheckoutDeliveryReview';
import { CheckoutStepLink } from './shared/CheckoutStepLink';
import { isMwCheckoutOptimizationsTestActive } from './helpers/mwCheckoutOptimizationsTestHelpers';

import { CheckoutProgressReview_buyer$key } from './__generated__/CheckoutProgressReview_buyer.graphql';
import { CheckoutProgressReview_deliveryOptions$key } from './__generated__/CheckoutProgressReview_deliveryOptions.graphql';
import { CheckoutProgressReview_item$key } from './__generated__/CheckoutProgressReview_item.graphql';
import { CheckoutProgressReview_transaction$key } from './__generated__/CheckoutProgressReview_transaction.graphql';

import styles from './styles/CheckoutProgressReview.scss';

const buyerFragment = graphql`
    fragment CheckoutProgressReview_buyer on User {
        ...CheckoutStepLink_buyer
        addressBook(partialAddressCheckoutEligibility: $partialAddressCheckoutEligibility) {
            addressList {
                serviceId
                ...CheckoutAddressReview_address
            }
        }
    }
`;

const itemFragment = graphql`
    fragment CheckoutProgressReview_item on Item {
        ...CheckoutDeliveryReview_item
    }
`;

const transactionFragment = graphql`
    fragment CheckoutProgressReview_transaction on Transaction {
        ...CheckoutDeliveryReview_transaction
        isNegotiable
    }
`;

const deliveryOptionsFragment = graphql`
    fragment CheckoutProgressReview_deliveryOptions on ShipmentQuote @relay(plural: true) {
        serviceId
        ...CheckoutDeliveryReview_shipmentQuote
    }
`;

type CheckoutProgressReviewProps = {
    isComputing: boolean;
    buyer: CheckoutProgressReview_buyer$key | null;
    deliveryOptions: CheckoutProgressReview_deliveryOptions$key;
    item: CheckoutProgressReview_item$key;
    transaction: CheckoutProgressReview_transaction$key | null;
};

export const CheckoutProgressReview: FunctionComponent<CheckoutProgressReviewProps> = props => {
    const { isComputing } = props;

    const { isMobile } = useContext(CheckoutServerVarsContext);
    const paneState = useCheckoutSelector(state => state.pane);
    const shippingAddressId = useCheckoutSelector(state => state.transaction.shippingAddressId);
    const shipmentQuoteId = useCheckoutSelector(state => state.transaction.shipmentQuoteId);

    const buyer = useFragment(buyerFragment, props.buyer);
    const deliveryOptions = useFragment(deliveryOptionsFragment, props.deliveryOptions);
    const item = useFragment(itemFragment, props.item);
    const transaction = useFragment(transactionFragment, props.transaction);

    const shippingAddress =
        buyer?.addressBook?.addressList?.find(
            address => address?.serviceId === shippingAddressId
        ) || null;
    const shipmentQuote =
        deliveryOptions?.find(quote => quote?.serviceId === shipmentQuoteId) || null;

    const isShippingCompleted = isPaneClickable(paneState, PANES.PANE_SHIPPING);
    const isDeliveryCompleted =
        isShippingCompleted && isPaneClickable(paneState, PANES.PANE_DELIVERY);

    if (isMobile && isMwCheckoutOptimizationsTestActive() && isOnReviewPane(paneState)) {
        const headerClasses = classnames(
            dibsCss.sassyFontHeaderSmall,
            dibsCss.mtMedium,
            dibsCss.mbSmall
        );

        if (transaction?.isNegotiable) {
            return (
                <h2 className={headerClasses}>
                    <FormattedMessage
                        id="checkout.CheckoutProgressReview.review_your_offer"
                        defaultMessage="Review Your Offer"
                    />
                </h2>
            );
        } else {
            return (
                <h2 className={headerClasses}>
                    <FormattedMessage
                        id="checkout.CheckoutProgressReview.review_your_order"
                        defaultMessage="Review Your Order"
                    />
                </h2>
            );
        }
    }

    const showCompletedSteps =
        (isShippingCompleted || isDeliveryCompleted) && !isOnReviewPane(paneState);

    if (!showCompletedSteps) {
        const mainSeparatorClassName = classnames(styles.mainSeparator, {
            [styles.isMobile]: isMobile,
        });
        return <hr className={mainSeparatorClassName} />;
    }

    const actionLabel = (
        <FormattedMessage
            id="checkout.CheckoutProgressReview.change_label"
            defaultMessage="Change"
        />
    );

    return (
        <div className={styles.wrapper}>
            {isShippingCompleted && (
                <div className={styles.step}>
                    <div className={styles.label}>
                        <FormattedMessage
                            id="checkout.CheckoutProgressReview.shipping_label"
                            defaultMessage="Shipping Address"
                        />
                    </div>
                    <div className={styles.content}>
                        <CheckoutAddressReview address={shippingAddress} />
                    </div>
                    <div className={styles.action}>
                        <CheckoutStepLink
                            buyer={buyer}
                            isComputing={isComputing}
                            step={PANES.PANE_SHIPPING}
                        >
                            {actionLabel}
                        </CheckoutStepLink>
                    </div>
                </div>
            )}
            {isDeliveryCompleted && (
                <>
                    <hr className={styles.separator} />
                    <div className={styles.step}>
                        <div className={styles.label}>
                            <FormattedMessage
                                id="checkout.CheckoutProgressReview.delivery_label"
                                defaultMessage="Shipping Method"
                            />
                        </div>
                        <div className={styles.content}>
                            <CheckoutDeliveryReview
                                item={item}
                                transaction={transaction}
                                shipmentQuote={shipmentQuote}
                            />
                        </div>
                        <div className={styles.action}>
                            <CheckoutStepLink
                                buyer={buyer}
                                isComputing={isComputing}
                                step={PANES.PANE_DELIVERY}
                            >
                                {actionLabel}
                            </CheckoutStepLink>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};
